/**
 * ResultsScreen - Final screen showing Shopping List and Answer Report
 * Systembolaget-inspired clean, minimal design
 */

import React, { useState } from 'react';
import { colors, fonts, spacing, radii, layout, transitions } from '../styles/theme';
import { ReportItem, ShoppingListItem, PHASE_LABELS, Phase } from '../types';
import ShoppingListView from './ShoppingListView';
import ReportView from './ReportView';
import { generateASCIIReport } from '../utils/generateASCIIReport';

interface ResultsScreenProps {
    phase: Phase;
    area: string;
    report: ReportItem[];
    shoppingList: ShoppingListItem[];
    onRestart: () => void;
    onGiveFeedback?: () => void;
    participantOneName?: string;
    participantTwoName?: string;
}

type TabType = 'shopping-list' | 'report';

const ResultsScreen: React.FC<ResultsScreenProps> = ({
    phase,
    area,
    report,
    shoppingList,
    onRestart,
    onGiveFeedback,
    participantOneName,
    participantTwoName,
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('shopping-list');

    return (
        <div style={styles.container}>
            {/* Header */}
            <header style={styles.header}>
                <div style={styles.headerContent}>
                    <h1 style={styles.title}>Shopping Bag</h1>
                    <p style={styles.subtitle}>
                        {PHASE_LABELS[phase]} · {area}
                        {participantOneName && participantTwoName && (
                            <span> · {participantOneName} &amp; {participantTwoName}</span>
                        )}
                    </p>
                </div>
            </header>

            {/* Download and actions – placed at the very top for visibility */}
            <div style={styles.downloadBar}>
                <div style={styles.downloadBarInner}>
                    <button 
                        style={styles.pdfButton} 
                        onClick={() => void generateASCIIReport({
                            phase,
                            phaseLabel: PHASE_LABELS[phase],
                            area,
                            shoppingList,
                            report,
                            participantOneName: participantOneName || '',
                            participantTwoName: participantTwoName || '',
                        })}
                    >
                        <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                            style={{ marginRight: spacing[2] }}
                        >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download Report
                    </button>
                    <button style={styles.restartButton} onClick={onRestart}>
                        <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                            style={{ marginRight: spacing[2] }}
                        >
                            <polyline points="1 4 1 10 7 10" />
                            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                        </svg>
                        Start New Assessment
                    </button>
                </div>
                <p style={styles.downloadNote}>
                    Your data is not stored on our servers. Download the report to keep a copy of your results.
                </p>
            </div>

            {/* Tabs */}
            <nav style={styles.tabsContainer}>
                <div style={styles.tabs}>
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'shopping-list' ? styles.tabActive : {})
                        }}
                        onClick={() => setActiveTab('shopping-list')}
                    >
                        Shopping List
                        <span style={{
                            ...styles.tabCount,
                            ...(activeTab === 'shopping-list' ? styles.tabCountActive : {})
                        }}>
                            {shoppingList.length}
                        </span>
                    </button>
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'report' ? styles.tabActive : {})
                        }}
                        onClick={() => setActiveTab('report')}
                    >
                        Full Report
                        <span style={{
                            ...styles.tabCount,
                            ...(activeTab === 'report' ? styles.tabCountActive : {})
                        }}>
                            {report.length}
                        </span>
                    </button>
                </div>
            </nav>

            {/* Tab Content */}
            <main style={styles.content}>
                <div style={styles.contentInner}>
                    {activeTab === 'shopping-list' && (
                        <ShoppingListView
                            items={shoppingList}
                            phase={PHASE_LABELS[phase]}
                            area={area}
                        />
                    )}
                    {activeTab === 'report' && (
                        <ReportView
                            items={report}
                            phase={PHASE_LABELS[phase]}
                            area={area}
                        />
                    )}
                </div>

                {/* Feedback CTA */}
                {onGiveFeedback && (
                    <div style={styles.feedbackCta}>
                        <p style={styles.feedbackCtaText}>
                            Help us improve this tool – share your feedback.
                        </p>
                        <button style={styles.feedbackCtaButton} onClick={onGiveFeedback}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', flexShrink: 0 }}>
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                            Give feedback (5 min)
                        </button>
                    </div>
                )}

                {/* Contact cards */}
                <section style={styles.contactSection}>
                    <h2 style={styles.contactHeading}>Any questions or feedback? Get in touch with us.</h2>
                    <div style={styles.contactCardsRow}>
                        <div style={styles.personCard}>
                            <img
                                src={`${process.env.PUBLIC_URL || ''}/image.png`}
                                alt="Laurynas Adomaitis"
                                style={styles.personImage}
                            />
                            <h3 style={styles.personName}>Laurynas Adomaitis</h3>
                            <p style={styles.personRole}>Ethics researcher, RISE</p>
                            <div style={styles.personLinks}>
                                <a
                                    href="https://www.linkedin.com/in/laurynas-adomaitis/"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={styles.linkButton}
                                >
                                    LinkedIn
                                </a>
                                <a
                                    href="mailto:laurynas.adomaitis@ri.se?subject=Shopping%20Bag%20Tool"
                                    style={styles.linkSecondaryButton}
                                >
                                    Email
                                </a>
                            </div>
                        </div>
                        <div style={styles.personCard}>
                            <img
                                src={`${process.env.PUBLIC_URL || ''}/image%20copy.png`}
                                alt="Emmie Nordell"
                                style={styles.personImage}
                            />
                            <h3 style={styles.personName}>Emmie Nordell</h3>
                            <p style={styles.personRole}>Legal researcher, RISE</p>
                            <div style={styles.personLinks}>
                                <a
                                    href="https://www.linkedin.com/in/emmie-nordell-16154b60/"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={styles.linkButton}
                                >
                                    LinkedIn
                                </a>
                                <a
                                    href="mailto:emmie.nordell@ri.se?subject=Shopping%20Bag%20Tool"
                                    style={styles.linkSecondaryButton}
                                >
                                    Email
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Funding disclaimer */}
                <div style={styles.disclaimer}>
                    <img
                        src={`${process.env.PUBLIC_URL || ''}/vinnova-logo.svg`}
                        alt="Vinnova"
                        style={styles.vinnovaLogo}
                    />
                    <p style={styles.disclaimerText}>
                        Funded under <em>"Legal and ethical analysis and operational guidelines for Virtual Reality"</em> by Vinnova.
                        Reference number 2025-00489. Coordinator: RISE Research Institutes of Sweden AB.
                    </p>
                </div>
            </main>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    container: {
        minHeight: '100vh',
        backgroundColor: colors.bgLight,
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.border}`,
        padding: `${spacing[8]} ${spacing[6]}`,
    },
    headerContent: {
        maxWidth: layout.maxWidth,
        margin: '0 auto',
        textAlign: 'center',
    },
    title: {
        fontSize: fonts['2xl'],
        fontWeight: fonts.bold,
        color: colors.text,
        marginBottom: spacing[2],
    },
    subtitle: {
        fontSize: fonts.base,
        color: colors.textSecondary,
        margin: 0,
    },
    tabsContainer: {
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.border}`,
    },
    tabs: {
        display: 'flex',
        justifyContent: 'center',
        gap: spacing[1],
        padding: `${spacing[3]} ${spacing[6]}`,
        maxWidth: layout.maxWidth,
        margin: '0 auto',
    },
    tab: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing[2],
        padding: `${spacing[3]} ${spacing[5]}`,
        fontSize: fonts.sm,
        fontWeight: fonts.medium,
        color: colors.textSecondary,
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: '2px solid transparent',
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
    },
    tabActive: {
        color: colors.text,
        borderBottomColor: colors.text,
    },
    tabCount: {
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        backgroundColor: colors.bgLight,
        color: colors.textMuted,
        padding: `${spacing[0.5]} ${spacing[2]}`,
        borderRadius: radii.full,
    },
    tabCountActive: {
        backgroundColor: colors.text,
        color: colors.white,
    },
    content: {
        flex: 1,
        padding: spacing[6],
    },
    contentInner: {
        maxWidth: layout.contentWidth,
        margin: '0 auto',
    },
    contactSection: {
        marginTop: spacing[8],
        paddingTop: spacing[8],
        borderTop: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    contactHeading: {
        fontSize: fonts.base,
        fontWeight: fonts.medium,
        color: colors.text,
        marginBottom: spacing[4],
    },
    contactCardsRow: {
        display: 'flex',
        gap: spacing[4],
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    personCard: {
        backgroundColor: colors.white,
        borderRadius: radii.lg,
        border: `1px solid ${colors.border}`,
        padding: spacing[4],
        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
    },
    personImage: {
        width: '100%',
        height: 160,
        display: 'block',
        borderRadius: radii.md,
        objectFit: 'cover',
        marginBottom: spacing[3],
    },
    personName: {
        fontSize: fonts.base,
        fontWeight: fonts.semibold,
        color: colors.text,
        marginBottom: spacing[1],
    },
    personRole: {
        fontSize: fonts.sm,
        color: colors.textSecondary,
        marginBottom: spacing[3],
    },
    personLinks: {
        display: 'flex',
        gap: spacing[2],
        flexWrap: 'wrap',
    },
    linkButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[1]} ${spacing[3]}`,
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        color: colors.white,
        backgroundColor: colors.primary,
        borderRadius: radii.full,
        textDecoration: 'none',
    },
    linkSecondaryButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[1]} ${spacing[3]}`,
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        color: colors.textSecondary,
        backgroundColor: colors.bgLight,
        borderRadius: radii.full,
        textDecoration: 'none',
    },
    downloadBar: {
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.border}`,
        padding: `${spacing[5]} ${spacing[6]}`,
    },
    downloadBarInner: {
        display: 'flex',
        justifyContent: 'center',
        gap: spacing[4],
        flexWrap: 'wrap' as const,
        maxWidth: layout.maxWidth,
        margin: '0 auto',
    },
    downloadNote: {
        fontSize: fonts.sm,
        color: colors.textSecondary,
        textAlign: 'center' as const,
        margin: `${spacing[3]} auto 0`,
        maxWidth: layout.maxWidth,
    },
    pdfButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[3]} ${spacing[8]}`,
        fontSize: fonts.lg,
        fontWeight: fonts.semibold,
        color: colors.white,
        backgroundColor: colors.primary,
        border: 'none',
        borderRadius: radii.md,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        minHeight: layout.minTouchTarget,
    },
    restartButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[3]} ${spacing[6]}`,
        fontSize: fonts.base,
        fontWeight: fonts.medium,
        color: colors.text,
        backgroundColor: 'transparent',
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        minHeight: layout.minTouchTarget,
    },
    disclaimer: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[4],
        marginTop: spacing[8],
        padding: spacing[4],
        backgroundColor: colors.white,
        borderRadius: radii.lg,
        border: `1px solid ${colors.border}`,
        maxWidth: layout.contentWidth,
        margin: `${spacing[8]} auto 0`,
    },
    vinnovaLogo: {
        width: '80px',
        height: 'auto',
        flexShrink: 0,
    },
    disclaimerText: {
        fontSize: fonts.xs,
        color: colors.textMuted,
        lineHeight: fonts.lineHeightRelaxed,
        margin: 0,
    },
    feedbackCta: {
        marginTop: spacing[6],
        padding: `${spacing[5]} ${spacing[6]}`,
        backgroundColor: colors.bgWarm,
        borderRadius: radii.lg,
        border: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: spacing[3],
        textAlign: 'center' as const,
    },
    feedbackCtaText: {
        fontSize: fonts.base,
        fontWeight: fonts.medium,
        color: colors.text,
        margin: 0,
    },
    feedbackCtaButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[3]} ${spacing[6]}`,
        fontSize: fonts.base,
        fontWeight: fonts.semibold,
        color: colors.white,
        backgroundColor: colors.primary,
        border: 'none',
        borderRadius: radii.md,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        minHeight: layout.minTouchTarget,
    },
};

export default ResultsScreen;
